import posthog from "posthog-js";

const KEY = process.env.EXPO_PUBLIC_POSTHOG_KEY;
const HOST = process.env.EXPO_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";

const CONSENT_STORAGE_KEY = "coravu_cookie_consent";

let initialized = false;

/** Read the current consent decision from localStorage. Returns null on SSR
 *  or when the visitor hasn't chosen yet (in which case the banner is shown
 *  and PostHog stays uninitialized until they accept). */
function readConsent(): "accepted" | "essential" | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "accepted" || v === "essential" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Wire PostHog once on web. Idempotent — safe to call from layout
 * mount paths. SSR + missing-key are no-ops; keeps dev / preview
 * environments quiet without requiring a stub module. Autocapture is
 * OFF by design — we send explicit `track()` calls only, which keeps
 * signal/noise high and avoids accidentally capturing input contents.
 *
 * Consent gate: if the visitor chose "Essential only" we never call
 * posthog.init at all. If they haven't chosen yet, we also defer —
 * the banner will trigger init via `acceptAllCookies()` once they
 * accept. This means declined visitors get zero PostHog payloads on
 * subsequent visits, not just the first one.
 */
export function initAnalytics() {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!KEY) return;
  const consent = readConsent();
  if (consent !== "accepted") return;
  posthog.init(KEY, {
    api_host: HOST,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false,
    persistence: "localStorage",
    mask_all_text: false,
    respect_dnt: true,
  });
  initialized = true;
}

/** Called by the cookie banner's "Accept all" button. Persists the decision
 *  and lazily kicks off PostHog init (which was deferred while consent was
 *  null). Idempotent. */
export function acceptAllCookies() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, "accepted");
  } catch {
    // Private mode / quota — fall through; analytics simply stays off.
  }
  initAnalytics();
}

/** Called by the cookie banner's "Essential only" button. Persists the
 *  decision and, if PostHog had somehow been initialized earlier this
 *  session, opts out of capture. Sentry stays on — error monitoring is
 *  essential, not ad tracking. */
export function declineNonEssentialCookies() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, "essential");
  } catch {
    // Same logic as accept — best-effort persistence.
  }
  if (initialized) {
    try {
      posthog.opt_out_capturing();
    } catch {
      // posthog-js may not have a session; safe to swallow.
    }
  }
}

/** Read consent decision so the banner can decide whether to show. */
export function getCookieConsent(): "accepted" | "essential" | null {
  return readConsent();
}

export function identifyUser(
  user: { id: string; email?: string; role?: string } | null
): void {
  if (!initialized) return;
  if (!user) {
    posthog.reset();
    return;
  }
  posthog.identify(user.id, {
    email: user.email,
    role: user.role,
  });
}

/**
 * Closed event taxonomy. Adding a new event name? Add it here so the
 * type checker prevents drift across surfaces. Values are deliberately
 * snake_case product events, not raw method names — they read cleanly
 * in PostHog filters.
 */
export type EventName =
  | "sign_up_completed"
  | "sign_in_completed"
  | "competition_created"
  | "competition_status_changed"
  | "student_registered_for_competition"
  | "submission_submitted"
  | "judge_score_submitted"
  | "winner_selection_approved"
  | "interview_offer_extended"
  | "interview_offer_responded"
  | "inquiry_submitted"
  | "pricing_page_viewed"
  | "faq_page_viewed"
  | "discovery_call_cta_clicked"
  | "not_found_page_viewed"
  // Server-side event emitted by the welcome-email Edge Function.
  // Listed here for type-system consistency even though it's never
  // fired from the client bundle.
  | "welcome_email_sent";

/**
 * Privacy boundary: pass IDs / status enums / counts only. Don't pass
 * full row data (submission text, user-typed messages, etc.). We're
 * measuring product usage, not exfiltrating content.
 */
export function track(event: EventName, props?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, props);
}
